import { useEffect, useState } from "react";
import axiosInstance from "../../services/axios";
import { FormDto, Category } from "../../types/category.types";
import styles from "../css/ChildForms.module.css";
import { showError, showConfirm, showWarning } from "../../utils/notifications";
import { safeLogger } from "../../utils/logger";
import { baseURL } from "../../services/axios";

interface ChildFormsProps {
  childId: number;
  childName?: string;
}

export const ChildForms = ({ childId, childName }: ChildFormsProps) => {
  const [forms, setForms] = useState<FormDto[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editFileName, setEditFileName] = useState<string>("");
  const [editFile, setEditFile] = useState<File | null>(null);
  const [editCategoryId, setEditCategoryId] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMounted, setIsMounted] = useState(true);
  
  // סטייטים להעלאת טפסים
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadCategoryId, setUploadCategoryId] = useState<number>(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
    
    return () => {
      setIsMounted(false);
    };
  }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    if (!isMounted) return;
    try {
      setLoading(true);
      const [formsRes, categoriesRes] = await Promise.all([
        axiosInstance.get(`/Child/${childId}/forms`),
        axiosInstance.get(`/FormCategory`),
      ]);

      if (!isMounted) return; // בדיקה אם הקומפוננטה עדיין mounted

      setCategories(categoriesRes.data);
      setForms(
        formsRes.data.map((form: any) => {
          // תמיכה בשמות שדה שונים מהשרת
          let formCategoryId = form.formCategoryId ?? form.formCategoryCode ?? form.FormCategoryCode;
          if (typeof formCategoryId === "string") formCategoryId = Number(formCategoryId);
          const found = categoriesRes.data.find((c: Category) => c.code === formCategoryId);
          return { ...form, formCategoryId, categoryName: found?.categoryName || "" };
        })
      );
    } catch (error) {
      console.error("Error loading child forms", error);
      if (isMounted) {
        setError("שגיאה בטעינת הטפסים");
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!showConfirm("האם למחוק את הטופס?")) return;
    try {
  await axiosInstance.delete(`/Form/${id}`);
  setForms(forms => forms.filter(f => f.Id !== id));
    } catch (err) {
      showError("שגיאה במחיקה");
    }
  };

  const handleEdit = (idx: number) => {
    const form = forms[idx];
  setEditIndex(idx);
  setEditFileName(form.FileName);
  setEditCategoryId(form.FormCategoryId ?? 0);
  setEditFile(null);
  };

  const handleSaveEdit = async (form: FormDto) => {
    try {
  if (!form.Id) {
        showError("מזהה הטופס חסר");
        return;
      }
      const formData = new FormData();
  formData.append("Id", form.Id.toString());
  formData.append("FileName", editFileName);
  formData.append("FormCategoryCode", editCategoryId.toString());
      if (editFile) {
        formData.append("FormFile", editFile);
      }
  await axiosInstance.put(`/Form/${form.Id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await fetchData();
      setEditIndex(null);
      setEditFileName("");
      setEditFile(null);
      setEditCategoryId(0);
    } catch (err) {
      showError("שגיאה בעדכון");
    }
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditFile(null);
  };

  const handleUploadForm = async () => {
    if (!uploadFile || !uploadFileName || !uploadCategoryId) {
      showWarning("יש למלא את כל השדות");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("FormFile", uploadFile);
      formData.append("FileName", uploadFileName);
      formData.append("FormCategoryCode", uploadCategoryId.toString());
      formData.append("ChildCode", childId.toString());

      const response = await axiosInstance.post("/Form", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      // הוסף את הטופס החדש לטבלה מיידית
      const newForm = response.data;
      let formCategoryId = newForm.formCategoryId || uploadCategoryId;
      if (typeof formCategoryId === "string") formCategoryId = Number(formCategoryId);
      const found = categories.find(c => c.code === formCategoryId);
      const categoryName = found?.categoryName || "";
      
      setForms(prev => [
        ...prev,
        { ...newForm, categoryName, formCategoryId }
      ]);

      // איפוס הטופס
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadFileName("");
      setUploadCategoryId(0);
      
    } catch (error) {
      safeLogger.error("Error uploading form");
      showError("שגיאה בהעלאת הטופס");
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setShowUploadForm(false);
    setUploadFile(null);
    setUploadFileName("");
    setUploadCategoryId(0);
  };

  if (loading) return <div className={styles.loading}>טוען טפסים...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.container}>
      {childName && <h3 className={styles.title}>טפסי {childName}</h3>}
      
      {/* טבלת הטפסים */}
      <div className={styles.sectionBox}>
        <div className={styles.tableContainer}>
          <table className={styles.formsTable}>
            <thead>
              <tr>
                <th>מחיקה</th>
                <th>פעולות</th>
                <th>קטגוריה</th>
                <th>טופס</th>
              </tr>
            </thead>
            <tbody>
              {forms.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.noData}>
                    אין טפסים עדיין
                  </td>
                </tr>
              ) : (
                forms.map((f, i) => (
                  <tr key={i}>
                    <td>
                      <button className={styles.actionBtn} title="מחיקה" onClick={() => f.Id !== undefined ? handleDelete(f.Id) : null}>
                          <span role="img" aria-label="delete">🗑️</span>
                      </button>
                    </td>
                    <td>
                      {editIndex === i ? (
                        <>
                          <button className={styles.actionBtn} title="שמירה" onClick={() => handleSaveEdit(f)}>
                            ✔️
                          </button>
                          <button className={styles.actionBtn} title="ביטול" onClick={handleCancelEdit}>
                            ❌
                          </button>
                        </>
                      ) : (
                        <button className={styles.actionBtn} title="עריכה" onClick={() => handleEdit(i)}>
                          <span role="img" aria-label="edit">✏️</span>
                        </button>
                      )}
                    </td>
                    <td>
                      {editIndex === i ? (
                        <select
                          value={editCategoryId}
                          onChange={e => {
                            const id = Number(e.target.value);
                            setEditCategoryId(id);
                          }}
                        >
                          <option value="">בחר קטגוריה</option>
                          {categories.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.categoryName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        (() => {
                            const foundCategory = categories.find(c => c.code === f.FormCategoryId);
                          return foundCategory?.categoryName || f.FormCategoryId;
                        })()
                      )}
                    </td>
                    <td>
                      {editIndex === i ? (
                        <>
                          <input
                            value={editFileName}
                            onChange={e => setEditFileName(e.target.value)}
                            style={{ width: "60%" }}
                          />
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.odt,.csv,.zip,.rar,.7z,.xml,.json,.html,.md,.pages"
                            onChange={e => setEditFile(e.target.files?.[0] || null)}
                            style={{ marginRight: 8 }}
                          />
                        </>
                      ) : (
                        <>
                          <a
                              href={`${baseURL}${f.FileUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.viewLink}
                          >
                            לצפייה בטופס
                          </a>
                          {" " + f.FileName}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* כפתור העלאת טופס */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        {!showUploadForm ? (
          <button
            onClick={() => setShowUploadForm(true)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              padding: '15px 30px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 8px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: '0 auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 20px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 15px rgba(102, 126, 234, 0.3)';
            }}
          >
            <span style={{ fontSize: '20px' }}>📄</span>
            העלה טופס
          </button>
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '25px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            maxWidth: '500px',
            margin: '0 auto',
            border: '2px solid #e1e5e9'
          }}>
            <h4 style={{ 
              color: '#333', 
              marginBottom: '20px',
              textAlign: 'center',
              fontSize: '20px'
            }}>
              העלאת טופס חדש
            </h4>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: '#555'
              }}>
                שם הטופס:
              </label>
              <input
                type="text"
                value={uploadFileName}
                onChange={(e) => setUploadFileName(e.target.value)}
                placeholder="הכנס שם לטופס"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: '#555'
              }}>
                קטגוריה:
              </label>
              <select
                value={uploadCategoryId}
                onChange={(e) => setUploadCategoryId(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value={0}>בחר קטגוריה</option>
                {categories.map((category) => (
                  <option key={category.code} value={category.code}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: '#555'
              }}>
                קובץ:
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.odt,.csv,.zip,.rar,.7z,.xml,.json,.html,.md,.pages"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  backgroundColor: 'white'
                }}
              />
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '15px', 
              justifyContent: 'center' 
            }}>
              <button
                onClick={handleUploadForm}
                disabled={uploading || !uploadFile || !uploadFileName || !uploadCategoryId}
                style={{
                  background: uploading ? '#ccc' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '12px 25px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {uploading ? 'מעלה...' : 'אשר'}
              </button>
              <button
                onClick={handleCancelUpload}
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '12px 25px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                ביטול
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
